import React, { useEffect, useState } from 'react';
import axiosInstance from '../../utils/axiosInstance';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
} from '@mui/material';
import Loading from '../common/Loading';
import { useNavigate } from 'react-router-dom';

const InOut = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  const fetchOrders = async () => {
    setError(false);
    setLoading(true);
    try {
      const response = await axiosInstance.get('/order/search?status=delivered,confirmed');
      setOrders(response.data.data);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axiosInstance.put(`/order/${orderId}`, { status: newStatus });
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order.uuid === orderId ? { ...order, status: newStatus } : order
        )
      );
    } catch (err) {
      console.error('Error updating order status:', err);
    }
  };

  const readyToDeliver = orders.filter(order => order.status === 'confirmed');
  const awaitingReturn = orders.filter(order => order.status === 'delivered');

  if (loading) return <Loading size="sm" />;

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-center">
        <p className="text-red-600 text-sm">Failed to load orders</p>
        <button
          onClick={fetchOrders}
          className="mt-2 text-sm text-red-700 underline hover:no-underline"
        >
          Retry
        </button>
      </div>
    );
  }

  const renderOrderList = (ordersList) => (
    <List sx={{ p: 0 }}>
      {ordersList.length === 0 ? (
        <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
          No orders found.
        </Typography>
      ) : (
        ordersList.map(order => (
          <ListItem
            key={order.uuid}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              borderBottom: '1px solid #eee',
              py: 1.5,
              px: 1,
              gap: 1,
            }}
          >
            <Box sx={{ flexGrow: 1, width: '100%' }}>
              <ListItemText
                primary={
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { color: 'primary.main', textDecoration: 'underline' },
                    }}
                    onClick={() => navigate(`/orders/${order.uuid}`)}
                  >
                    Order #{order.formattedOrderNumber} — {order.customer?.firstname + ' ' + order.customer?.lastname || 'Unknown Customer'}
                  </Typography>
                }
                secondary={
                  <div>
                    <Typography variant="body2" color="textSecondary">
                      Current Status: {order.status}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Start Date: {order.startDate}
                    </Typography>
                  </div>
                }
              />
            </Box>

            <Box sx={{ width: { xs: '100%', sm: 160 } }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={order.status}
                  label="Status"
                  onChange={(e) => updateOrderStatus(order.uuid, e.target.value)}
                >
                  <MenuItem value="confirmed">Confirmed</MenuItem>
                  <MenuItem value="delivered">Delivered</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </ListItem>
        ))
      )}
    </List>
  );

  return (
    <Card sx={{ borderRadius: 2, boxShadow: 2, overflow: 'hidden' }}>
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<span>▼</span>}>
          <Typography component="span" fontWeight={600}>
            Ready to Deliver
            <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-bold">
              {readyToDeliver.length}
            </span>
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <CardContent sx={{ pt: 0 }}>
            {renderOrderList(readyToDeliver)}
          </CardContent>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<span>▼</span>}>
          <Typography component="span" fontWeight={600}>
            Awaiting Return
            <span className="ml-2 px-2 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-bold">
              {awaitingReturn.length}
            </span>
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <CardContent sx={{ pt: 0 }}>
            {renderOrderList(awaitingReturn)}
          </CardContent>
        </AccordionDetails>
      </Accordion>
    </Card>
  );
};

export default InOut;
